// Session 38 Phase 2 · Admin Workspace Edit — parent venue banner + move action
import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import AdminPhotoUploader from "../components/AdminPhotoUploader";
import ExternalBookingSection from "../components/ExternalBookingSection";
import api from "../lib/api";
import { toast } from "sonner";

const WS_TYPES = ["Coworking Space", "Event Venue", "Meeting Room", "Private Office"];

export default function AdminWorkspaceEdit() {
  const { workspaceId } = useParams();
  const nav = useNavigate();
  const [ws, setWs] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showMove, setShowMove] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/workspaces/${workspaceId}`);
      setWs(r.data);
      setForm({
        name: r.data.name || "",
        workspace_type: r.data.workspace_type || "",
        description: r.data.description || "",
        capacity: r.data.capacity ?? "",
        hourly: r.data.hourly ?? "",
        daily: r.data.daily ?? "",
        weekly: r.data.weekly ?? "",
        monthly: r.data.monthly ?? "",
        amenities: (r.data.amenities || []).join(", "),
        photos: (r.data.photos || []).join("\n"),
        published: !!r.data.published,
        status: r.data.status || "active",
        booking_mode: r.data.booking_mode || "instant",
        // Session 49 · External booking config
        management_mode: r.data.management_mode || "metier_managed",
        external_booking_url: r.data.external_booking_url || "",
        booking_cta_override: r.data.booking_cta_override || "",
        cta_disclosure: r.data.cta_disclosure || "",
        open_in_new_tab: r.data.open_in_new_tab !== false,
        track_external_clicks: r.data.track_external_clicks !== false,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load workspace");
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); };

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      ["capacity", "hourly", "daily", "weekly", "monthly"].forEach(k => {
        if (payload[k] === "" || payload[k] == null) payload[k] = null;
        else payload[k] = Number(payload[k]);
      });
      payload.amenities = form.amenities
        ? form.amenities.split(",").map(s => s.trim()).filter(Boolean) : [];
      payload.photos = form.photos
        ? form.photos.split("\n").map(s => s.trim()).filter(Boolean) : [];
      await api.put(`/admin/workspaces/${workspaceId}`, payload);
      toast.success("Workspace saved");
      setDirty(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!ws) return <AppShell><div className="m-container" style={{padding:60}}>Loading…</div></AppShell>;

  const p = ws.parent_venue;

  return (
    <AppShell>
      <div className="m-container" style={{ paddingTop: 32, paddingBottom: 80 }}
           data-testid="admin-workspace-edit-page">
        <div style={{ marginBottom: 4, letterSpacing: "0.18em", fontSize: 11,
                       textTransform: "uppercase", color: "rgba(27,35,64,0.55)" }}>
          <Link to="/admin/inventory/workspaces">Admin · Inventory · Workspaces</Link> · Edit
        </div>
        <h1 style={{ fontFamily: "Georgia, serif", fontSize: 36, margin: "6px 0 6px",
                      color: "var(--m-navy)" }}>
          {ws.name}
        </h1>
        <div style={{ display: "flex", gap: 8, fontSize: 12, color: "rgba(27,35,64,0.55)",
                       marginBottom: 20 }}>
          <span>ID: <code>{ws.workspace_id}</code></span>
          <span>· {ws.slug}</span>
          <span>· {ws.workspace_type || "—"}</span>
        </div>

        {/* Parent venue banner — always visible near top */}
        {p && (
          <div style={{ padding: "14px 18px", background: "rgba(184,145,58,0.10)",
                         border: "1px solid rgba(184,145,58,0.28)", borderRadius: 10,
                         marginBottom: 24, display: "flex",
                         justifyContent: "space-between", alignItems: "center", gap: 20 }}
                data-testid="admin-workspace-parent-banner">
            <div>
              <div style={{ fontSize: 11, letterSpacing: "0.14em",
                             textTransform: "uppercase", color: "rgba(27,35,64,0.60)" }}>
                Parent venue
              </div>
              <div style={{ marginTop: 2, fontWeight: 600, color: "var(--m-navy)" }}>
                <Link to={`/admin/venues/${p.venue_id}/edit`}
                       data-testid="admin-workspace-parent-link">{p.name}</Link>
              </div>
              <div style={{ marginTop: 2, fontSize: 13, color: "rgba(27,35,64,0.72)" }}>
                Location: {[p.city, p.state, p.postal_code].filter(Boolean).join(", ") || "—"}
              </div>
            </div>
            <button type="button" onClick={() => setShowMove(s => !s)} className="m-btn-ghost"
                     style={{ fontSize: 13 }}
                     data-testid="admin-workspace-move-toggle">
              Move to another venue →
            </button>
          </div>
        )}

        {showMove && <MoveWorkspacePanel workspaceId={workspaceId} currentVenueId={p?.venue_id}
                                            onDone={() => { setShowMove(false); load(); }} />}

        <form onSubmit={save} style={{ display: "grid",
                                          gridTemplateColumns: "2fr 1fr", gap: 32 }}>
          <div>
            <SectionTitle>Basics</SectionTitle>
            <FieldGrid>
              <Field label="Workspace name" testid="ws-edit-name">
                <input value={form.name} onChange={e => set("name", e.target.value)} />
              </Field>
              <Field label="Workspace type" testid="ws-edit-type">
                <select value={form.workspace_type}
                         onChange={e => set("workspace_type", e.target.value)}>
                  <option value="">—</option>
                  {WS_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Description" full testid="ws-edit-desc">
                <textarea rows={4} value={form.description}
                           onChange={e => set("description", e.target.value)} />
              </Field>
              <Field label="Capacity (people)" testid="ws-edit-capacity">
                <input type="number" min="0" value={form.capacity}
                        onChange={e => set("capacity", e.target.value)} />
              </Field>
              <Field label="Booking mode" testid="ws-edit-booking-mode">
                <select value={form.booking_mode}
                         onChange={e => set("booking_mode", e.target.value)}>
                  <option value="instant">Instant book</option>
                  <option value="request">Request to book</option>
                </select>
              </Field>
            </FieldGrid>

            <SectionTitle>Pricing</SectionTitle>
            <FieldGrid>
              <Field label="Hourly rate ($)" testid="ws-edit-hourly">
                <input type="number" min="0" step="0.01" value={form.hourly}
                        onChange={e => set("hourly", e.target.value)} />
              </Field>
              <Field label="Half-day / daily ($)" testid="ws-edit-daily">
                <input type="number" min="0" step="0.01" value={form.daily}
                        onChange={e => set("daily", e.target.value)} />
              </Field>
              <Field label="Weekly ($)" testid="ws-edit-weekly">
                <input type="number" min="0" step="0.01" value={form.weekly}
                        onChange={e => set("weekly", e.target.value)} />
              </Field>
              <Field label="Monthly ($)" testid="ws-edit-monthly">
                <input type="number" min="0" step="0.01" value={form.monthly}
                        onChange={e => set("monthly", e.target.value)} />
              </Field>
            </FieldGrid>

            <SectionTitle>Amenities & Photos</SectionTitle>
            <FieldGrid>
              <Field label="Amenities (comma-separated)" full testid="ws-edit-amenities">
                <input value={form.amenities}
                        onChange={e => set("amenities", e.target.value)} />
              </Field>
              <Field label="Photo URLs (one per line)" full testid="ws-edit-photos">
                <textarea rows={3} value={form.photos}
                           onChange={e => set("photos", e.target.value)} />
                <AdminPhotoUploader
                  value={form.photos}
                  onChange={(v) => set("photos", v)}
                  testid="ws-edit"
                  previewName={form.name}
                  previewMeta={[ws?.parent_venue?.city, ws?.parent_venue?.state].filter(Boolean).join(", ")}
                  previewType={form.workspace_type}
                  previewSlug={ws?.slug}
                />
              </Field>
            </FieldGrid>

            <SectionTitle>Status</SectionTitle>
            <div style={{ display: "flex", gap: 18, alignItems: "center",
                           padding: "12px 16px", background: "rgba(27,35,64,0.03)",
                           border: "1px solid rgba(27,35,64,0.08)", borderRadius: 8 }}>
              <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}
                      data-testid="ws-edit-published-toggle">
                <input type="checkbox" checked={!!form.published}
                        onChange={e => set("published", e.target.checked)}
                        style={{ width: 16, height: 16 }} />
                <span>Published</span>
              </label>
              <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}
                      data-testid="ws-edit-status">
                <span>Status:</span>
                <select value={form.status}
                         onChange={e => set("status", e.target.value)}
                         style={{ padding: "6px 10px" }}>
                  <option value="active">active</option>
                  <option value="draft">draft</option>
                  <option value="paused">paused</option>
                  <option value="archived">archived</option>
                </select>
              </label>
            </div>

            <ExternalBookingSection form={form} set={set} slug={ws?.slug} scope="workspace" />

            <div style={{ marginTop: 30, display: "flex", gap: 12 }}>
              <button type="submit" className="m-btn-gold" disabled={saving}
                       data-testid="ws-edit-save">
                {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
              </button>
              <button type="button" onClick={() => nav("/admin/inventory/workspaces")}
                       className="m-btn-ghost" data-testid="ws-edit-back">
                Back to Workspaces
              </button>
            </div>
          </div>

          <aside>
            <SectionTitle>Inherited location</SectionTitle>
            <div style={{ padding: "14px 16px", background: "rgba(27,35,64,0.03)",
                           border: "1px solid rgba(27,35,64,0.08)", borderRadius: 8,
                           fontSize: 13, lineHeight: 1.7 }}
                  data-testid="ws-edit-inherited-location">
              <div><b>City:</b> {p?.city || "—"}</div>
              <div><b>State:</b> {p?.state || "—"}</div>
              <div><b>ZIP:</b> {p?.postal_code || "—"}</div>
              <div><b>Country:</b> {p?.country || "—"}</div>
              {p?.latitude != null && (
                <div><b>Lat/Lng:</b> {p.latitude.toFixed(4)}, {p.longitude.toFixed(4)}</div>
              )}
              <div style={{ marginTop: 10, color: "rgba(27,35,64,0.60)", fontSize: 12 }}>
                Address fields are managed on the parent venue.
                {p && (
                  <> <Link to={`/admin/venues/${p.venue_id}/edit`}>Edit venue address →</Link></>
                )}
              </div>
            </div>
          </aside>
        </form>
      </div>
    </AppShell>
  );
}


function MoveWorkspacePanel({ workspaceId, currentVenueId, onDone }) {
  const [target, setTarget] = useState("");
  const [venues, setVenues] = useState([]);
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/admin/venues?limit=500`);
        setVenues((r.data.venues || []).filter(v => v.venue_id !== currentVenueId));
      } catch { /* noop */ }
    })();
  }, [currentVenueId]);

  const move = async () => {
    if (!target) return;
    setBusy(true); setConflict(null);
    try {
      await api.post(`/admin/workspaces/${workspaceId}/move`,
                      { target_venue_id: target });
      toast.success("Workspace moved");
      onDone?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object"
          && detail.error === "workspace_has_future_bookings") {
        setConflict(detail);
      } else {
        toast.error(typeof detail === "string" ? detail : "Move failed");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: "16px 20px", background: "#fff",
                   border: "1px solid rgba(27,35,64,0.20)", borderRadius: 10,
                   marginBottom: 24 }}
         data-testid="admin-workspace-move-panel">
      <h4 style={{ margin: "0 0 8px", fontFamily: "Georgia, serif", fontSize: 20,
                    color: "var(--m-navy)" }}>Move workspace to another venue</h4>
      <p className="m-muted small" style={{ margin: "0 0 12px" }}>
        The workspace inherits its full address from the new parent. Historical bookings
        keep their original address snapshot. Future bookings will block the move.
      </p>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
        <label className="m-field" style={{ flex: 1, margin: 0 }}>
          <span style={{ fontSize: 11, letterSpacing: "0.14em",
                          textTransform: "uppercase",
                          color: "rgba(27,35,64,0.55)" }}>Target venue</span>
          <select value={target} onChange={e => setTarget(e.target.value)}
                   data-testid="admin-workspace-move-target"
                   style={{ padding: "8px 10px" }}>
            <option value="">Pick a venue…</option>
            {venues.map(v => (
              <option key={v.venue_id} value={v.venue_id}>
                {v.name} — {v.city}, {v.state} {v.postal_code}
              </option>
            ))}
          </select>
        </label>
        <button onClick={move} disabled={!target || busy} className="m-btn-gold"
                 style={{ padding: "9px 18px" }}
                 data-testid="admin-workspace-move-confirm">
          {busy ? "Moving…" : "Move"}
        </button>
      </div>
      {conflict && (
        <div style={{ marginTop: 14, padding: "12px 14px",
                       background: "rgba(180,60,60,0.08)",
                       border: "1px solid rgba(180,60,60,0.24)", borderRadius: 8,
                       fontSize: 13 }}
             data-testid="admin-workspace-move-conflict">
          <b>Move blocked — {conflict.conflicting_reservations?.length || 0} future
             reservation(s) must be resolved first:</b>
          <ul style={{ margin: "6px 0 0 20px", padding: 0 }}>
            {(conflict.conflicting_reservations || []).slice(0, 10).map(c => (
              <li key={c.request_id}>
                {c.date} {c.start_time} — {c.member_name} ({c.state})
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


function SectionTitle({ children }) {
  return <h3 style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "var(--m-navy)",
                       margin: "28px 0 12px" }}>{children}</h3>;
}
function FieldGrid({ children }) {
  return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
                        gap: "14px 18px" }}>{children}</div>;
}
function Field({ label, full, testid, children }) {
  return (
    <div style={{ gridColumn: full ? "1 / -1" : "auto" }} data-testid={testid}>
      <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                     color: "rgba(27,35,64,0.55)", marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}
